import X from "@mui/icons-material/X"
import { SvgIconProps, TextField as MuiTextField } from "@mui/material"
import { useFormikContext } from "formik"
import { Facebook, Instagram } from "lucide-mui"
import React, { useMemo } from "react"

import i18n from "@/i18n"

import { TikTokIcon } from "../../../shared/tiktok_icon"

const STYLES = {
  container: "bg-white rounded-lg p-6",
  header: "text-xl font-semibold mb-4 mt-0",
  socialGrid: "grid grid-cols-1 gap-4",
  socialItem: "flex items-center gap-3",
  iconWrapper: "flex-shrink-0 w-8 h-8 flex items-center justify-center",
  textFieldWrapper: "flex-1",
} as const

interface SocialAccountsFormValues {
  socialAccounts: Record<string, string>
}

const SocialAccounts: React.FC = () => {
  const SOCIAL_PLATFORMS = useMemo(
    () =>
      [
        {
          key: "facebook",
          label: i18n.t("app.settings.venues.tabbio.social_accounts.facebook_url"),
          icon: (props: SvgIconProps) => <Facebook htmlColor="#2563EB" {...props} />,
        },
        {
          key: "instagram",
          label: i18n.t("app.settings.venues.tabbio.social_accounts.instagram_url"),
          icon: (props: SvgIconProps) => <Instagram htmlColor="#DB2777" {...props} />,
        },
        { key: "x", label: i18n.t("app.settings.venues.tabbio.social_accounts.x_url"), icon: X },
        { key: "tiktok", label: i18n.t("app.settings.venues.tabbio.social_accounts.tiktok_url"), icon: TikTokIcon },
      ] as const,
    [i18n.locale],
  )

  const formik = useFormikContext<SocialAccountsFormValues>()

  const handleChange = (platform: string, value: string) => {
    void formik.setFieldValue(`socialAccounts.${platform}`, value)
  }

  return (
    <div className={STYLES.container} data-testid="settings-venue-social-container">
      <h2 className={STYLES.header}>{i18n.t("app.settings.venues.tabbio.social_accounts.header")}</h2>
      <div className={STYLES.socialGrid}>
        {SOCIAL_PLATFORMS.map((platform) => {
          const Icon = platform.icon
          const fieldName = `socialAccounts.${platform.key}`
          const value = formik.values.socialAccounts?.[platform.key] || ""
          const error = formik.errors.socialAccounts?.[platform.key]
          const touched = formik.touched.socialAccounts?.[platform.key]

          return (
            <div
              key={platform.key}
              className={STYLES.socialItem}
              data-testid={`settings-venue-social-item-${platform.key}`}
            >
              <div className={STYLES.iconWrapper}>
                <Icon className="w-5 h-5" />
              </div>
              <div className={STYLES.textFieldWrapper}>
                <MuiTextField
                  fullWidth
                  name={fieldName}
                  label={platform.label}
                  placeholder="https://..."
                  value={value}
                  onChange={(e) => handleChange(platform.key, e.target.value)}
                  onBlur={formik.handleBlur}
                  error={Boolean(touched && error)}
                  helperText={touched && error ? String(error) : ""}
                  data-testid={`settings-venue-social-input-${platform.key}`}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default SocialAccounts
